import classNames from "classnames";
import React, { Key, useEffect, useState } from "react";
import { If } from "react-conditional-components-renderer";

import { ignoreAttribute } from "@ascendnow/shared";

import { useChildrenArray } from "../../lib/helpers/hooks/useChildrenArray";
import AscDropdown from "../AscDropdown/AscDropdown";
import { AscDropdownOption } from "../AscDropdown/AscDropdownOptions/AscDropdownOptions";
import AscTab, { AscTabProps } from "./AscTab/AscTab";
import styles from "./AscTabs.module.scss";

type AscTabsType =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "admin";
export interface AscTabsProps<T = string | number> {
  className?: string;
  navigationClassName?: string;
  navigationListClassName?: string;
  children: JSX.Element[];
  selectedId?: T;
  onChange?: (selectedKey: T) => void;
  headless?: boolean;
  type?: AscTabsType;
  fullWidth?: boolean;
  tabContentClassName?: string;
  tabContentStyle?: React.CSSProperties;
  badgeClassName?: string;
  responsive?: boolean;
}

function AscTabs<T = string | number>({
  children,
  className,
  navigationClassName,
  navigationListClassName,
  onChange,
  selectedId,
  headless = false,
  type = "primary",
  fullWidth,
  tabContentClassName,
  tabContentStyle,
  responsive = true,
  badgeClassName,
}: AscTabsProps<T>) {
  const tabs = useChildrenArray<AscTabProps<T>>(children);
  const [selectedTab, setSelectedTab] = useState<AscTabProps<T>>();

  useEffect(() => {
    if (!selectedId) {
      setSelectedTab(tabs?.[0]);
      return;
    }
    const tab = tabs?.find((tab) => tab?.id === selectedId) || tabs?.[0];
    setSelectedTab(tab);

    //eslint-disable-next-line
  }, [selectedId, tabs]);

  const handleTabClick = (tab: AscTabProps<T>) => {
    if (tab?.disabled) {
      return;
    }

    /**
     * If you need to enable setSelectedTab:
     * make sure either: you dont make component accept prop to control state(currentTab)
     * or: you make component accept prop to control state(currentTab)
     * You will need to pick either internal or external state.
     * Both at some time will cause infinite update loop
     */

    if (onChange) {
      onChange?.(tab?.id);
    } else {
      setSelectedTab(tab);
    }

    if (tab.onClick) {
      tab.onClick();
    }
  };
  return (
    <div
      className={classNames(
        styles.ground,
        {
          [styles.primary]: type === "primary",
          [styles.secondary]: type === "secondary",
          [styles.tertiary]: type === "tertiary",
          [styles.quaternary]: type === "quaternary",
          [styles.admin]: type === "admin",
          [styles.fullWidth]: fullWidth,
          [styles.headless]: headless,
          [styles.responsive]: responsive,
        },
        className
      )}
    >
      <nav className={classNames(styles.tabsNavigation, navigationClassName)}>
        <ul
          className={classNames(
            styles.tabsNavigationList,
            navigationListClassName
          )}
        >
          {tabs?.map((tab) => {
            return (
              <li
                data-testid={tab?.testId}
                key={tab?.id as Key}
                onClick={() => handleTabClick(tab)}
                className={classNames(
                  styles.tabNavigationItem,
                  {
                    [styles.disabled]: tab?.disabled,
                    [styles.selected]: selectedTab?.id === tab?.id,
                    [styles.hidden]: tab?.hidden,
                    [styles.hasError]: tab?.hasError,
                  },
                  tab.className
                )}
                {...ignoreAttribute.contrast}
              >
                {tab?.title}
                {tab?.badgeCount ? (
                  <span
                    className={classNames(styles.badgeCount, badgeClassName)}
                  >
                    {tab?.badgeCount}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>
      <AscDropdown
        title={selectedTab?.title}
        className={styles.mobileDropdown}
        titleContainerClassName={styles.mobileDropdownTitleContainer}
        fullWidth
      >
        {tabs?.map((tab) => (
          <AscDropdownOption
            data-testid={tab?.testId}
            key={tab?.id as Key}
            onClick={() => handleTabClick(tab)}
            className={classNames(styles.dropdownItem, {
              [styles.disabled]: tab?.disabled,
              [styles.selected]: selectedTab?.id === tab?.id,
              [styles.hidden]: tab?.hidden,
            })}
            disabled={tab?.disabled}
          >
            {tab?.title}
          </AscDropdownOption>
        ))}
      </AscDropdown>
      <If test={!headless}>
        <div
          style={tabContentStyle}
          className={classNames(
            styles.selectedTabContent,
            tabContentClassName,
            selectedTab?.className
          )}
        >
          {selectedTab?.children}
        </div>
      </If>
    </div>
  );
}

AscTabs.Tab = AscTab;

export default AscTabs;
